/* ----------------------------------------------------------------------------
 * aiWritingLimit — PURE helpers for the monthly AI writing limit warning (owner
 * 12-10-2026 16:48 UTC, verbatim: "make sure limit warnings are displayed when
 * reached so i can increase if needed"). The server is authoritative: it refuses
 * with 402 ai_budget_reached and states the limit, the amount used and how to raise
 * it (the workspace setting name). The UI shows that clearly and, in voice, speaks
 * ONE short line. Used by the Aura writing cards (rail + popup), the recommendation
 * panel and the Generate panel.
 * -------------------------------------------------------------------------- */

export const AI_LIMIT_CODE = 'ai_budget_reached';
export const AI_LIMIT_TITLE = 'Monthly AI writing limit reached';

export interface AiLimit {
  cap: string;      // "$10.00"
  spent: string;    // "$10.02"
  setting: string;  // workspace setting that raises it
  message: string;  // the server's full sentence (limit, used, how to raise)
}

function str(v: unknown): string { return typeof v === 'string' ? v : ''; }

/** From a thrown API error (lib/api ApiError: status/code/message/payload, or a test
 *  shape {body:{error:{...}}}). Null unless it is the AI writing limit. */
export function aiLimitFromError(e: unknown): AiLimit | null {
  const any = e as { code?: unknown; message?: unknown; payload?: unknown; body?: { error?: Record<string, unknown> } } | null;
  if (!any) return null;
  const bodyErr = any.body?.error ?? (any.payload as { error?: Record<string, unknown> } | undefined)?.error ?? null;
  const code = str(bodyErr?.code) || str(any.code);
  if (code !== AI_LIMIT_CODE) return null;
  return {
    cap: str(bodyErr?.cap), spent: str(bodyErr?.spent), setting: str(bodyErr?.setting),
    message: str(bodyErr?.message) || str(any.message) || 'This month’s AI writing limit has been reached. Nothing was written.',
  };
}

/** From a suggest/propose `write` read-back (write unavailable because of the cap). */
export function aiLimitFromWrite(w: { available?: boolean; reason?: string; message?: string; cap?: string; spent?: string; setting?: string } | null | undefined): AiLimit | null {
  if (!w || w.available || w.reason !== AI_LIMIT_CODE) return null;
  return { cap: str(w.cap), spent: str(w.spent), setting: str(w.setting), message: str(w.message) };
}

/** ONE spoken line; the detail and how-to-raise stay on screen. */
export function limitSpokenLine(l: AiLimit): string {
  if (l.cap && l.spent) return `This month’s AI writing limit of ${l.cap} is reached, with ${l.spent} used. The details are on screen.`;
  return 'This month’s AI writing limit is reached. The details are on screen.';
}

/** Screen text: the server sentence, plus the setting if the sentence lacks it. */
export function limitScreenText(l: AiLimit): string {
  if (l.setting && !l.message.includes(l.setting)) {
    return `${l.message} To raise it, change the workspace setting ${l.setting} (ask the orchestrator to update it).`;
  }
  return l.message;
}
